<script setup lang="ts">
import { RouterView, useRoute, useRouter } from 'vue-router'
import { ref, onMounted, computed, watch } from 'vue'
import { Moon, Sunny, Menu as MenuIcon, Close } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import { useI18nLite } from '@/composables/useI18n'
import { useResponsive } from '@/composables/useResponsive'
import { useAuthStore } from '@/stores/auth'

const router = useRouter()
const route = useRoute()

// ALENS-107 #1: 只缓存确需保持状态的页面（它们用 onActivated 做回访刷新/恢复）。
// 其余视图离开即卸载，定时器 / SSE / echarts 走标准 onUnmounted 清理；
// 全量 keep-alive 会让 30 个视图及其资源永久常驻，长会话内存只增不减。
const KEEP_ALIVE_VIEWS = [
  'ReportsView',
  'AlertsView',
  'WatchlistView',
  'HistoryView',
  'ChatView',
  'PortfolioBacktestView',
  'PortfolioView',
  'BatchView',
  'TasksView',
]

const darkMode = ref(false)
const drawerOpen = ref(false)

// 登录态集中在 pinia store: LoginView 登录后头部用户 pill/退出按钮即时刷新,
// 不再依赖"mount 时读一次 localStorage" (旧实现首次登录后必须 F5 才显示用户态)
const auth = useAuthStore()
const currentUser = computed(() => auth.user)

const { lang, setLang, t } = useI18nLite()
const { isDesktop } = useResponsive()

const isLoginPage = computed(() => route.path === '/login')

function handleMenuSelect(index: string) {
  if (index && index.startsWith('/')) {
    router.push(index)
  }
}

const navItems = computed(() => {
  const items = [
    // 1. 宏观与市场
    { index: '/macro', label: t('nav.macro') },
    { index: '/sector-rrg', label: t('nav.sectorRrg') },
    { index: '/market', label: t('nav.market') },
    { index: '/fund-flow', label: t('nav.fundFlow') },
    { index: '/earnings-calendar', label: t('nav.earningsCalendar') },
    { index: '/reports', label: t('nav.reports') },

    // 2. 因子与标的
    { index: '/factors', label: t('nav.factors') },
    { index: '/factor-backtest', label: t('nav.factorBacktest') },
    { index: '/screener', label: t('nav.screener') },
    { index: '/funds', label: t('nav.funds') },
    { index: '/stock-compare', label: t('nav.stockCompare') },
    { index: '/watchlist', label: t('nav.watchlist') },

    // 3. 策略与回测
    { index: '/strategy-lab', label: t('nav.strategyLab') },
    { index: '/case-library', label: t('nav.caseLibrary') },
    { index: '/portfolio-backtest', label: t('nav.portfolioBacktest') },
    { index: '/options', label: t('nav.options') },
    { index: '/cbond', label: t('nav.cbond') },
    { index: '/arbitrage', label: t('nav.arbitrage') },

    // 4. 监控与执行
    { index: '/paper-deployments', label: t('nav.paperDeployments') },
    { index: '/anomaly-scanner', label: t('nav.anomalyScanner') },
    { index: '/alerts', label: t('nav.alerts') },

    // 5. 组合与风控
    { index: '/portfolio', label: t('nav.portfolio') },
    { index: '/retrospective', label: t('nav.retrospective') },

    // 6. 投研工作台
    { index: '/', label: t('nav.analyze') },
    { index: '/thesis', label: t('nav.thesisWorkbench') },
    { index: '/auto-research', label: t('nav.autoResearch') },
    { index: '/research-ops', label: t('nav.researchOps') },
    { index: '/chat', label: t('nav.chat') },
    { index: '/batch', label: t('nav.batch') },
    { index: '/tasks', label: t('nav.tasks') },
    { index: '/history', label: t('nav.history') },
  ]
  if (currentUser.value?.role === 'admin') {
    items.push(
      { index: '/settings', label: t('nav.settings') },
      { index: '/agent-backtest', label: t('nav.agentBacktest') },
    )
  }
  return items
})

const activeMenu = computed(() => route.path)

watch(
  () => route.path,
  () => {
    if (drawerOpen.value) drawerOpen.value = false
  },
)

function applyTheme(dark: boolean) {
  if (dark) {
    document.documentElement.classList.add('dark')
  } else {
    document.documentElement.classList.remove('dark')
  }
  localStorage.setItem('alphalens:theme', dark ? 'dark' : 'light')
}

function toggleTheme() {
  darkMode.value = !darkMode.value
  applyTheme(darkMode.value)
  ElMessage.success(t(darkMode.value ? 'theme.switched.toDark' : 'theme.switched.toLight'))
}

function toggleLang() {
  const nextLang = lang.value === 'zh' ? 'en' : 'zh'
  setLang(nextLang)
  ElMessage.success(t(nextLang === 'zh' ? 'auth.toast.langZh' : 'auth.toast.langEn'))
}

async function restoreSession() {
  const ok = await auth.restore()
  if (!ok && route.path !== '/login') {
    router.push({ name: 'login', query: { redirect: route.fullPath } })
  }
}

function handleLogout() {
  auth.logout()
  ElMessage.success(t('auth.toast.loggedOut'))
  router.push('/login')
}

onMounted(() => {
  const saved = localStorage.getItem('alphalens:theme')
  if (saved === 'dark') {
    darkMode.value = true
  } else if (saved === null) {
    darkMode.value = window.matchMedia('(prefers-color-scheme: dark)').matches
  }
  applyTheme(darkMode.value)
  restoreSession()
})
</script>

<template>
  <div v-if="isLoginPage" class="login-layout">
    <RouterView />
  </div>

  <el-container v-else class="layout">
    <!-- 桌面端 header -->
    <el-header v-if="isDesktop()" class="header">
      <div class="brand">
        <span class="logo">alphalens</span>
        <span class="tag">{{ t('nav.brand') }}</span>
      </div>
      <el-menu mode="horizontal" router :default-active="activeMenu" class="nav-menu" @select="handleMenuSelect">
        <!-- 1. 宏观与市场 -->
        <el-sub-menu index="macro-market-group">
          <template #title><span>{{ t('nav.group.macroMarket') }}</span></template>
          <el-menu-item index="/macro">{{ t('nav.macro') }}</el-menu-item>
          <el-menu-item index="/sector-rrg">{{ t('nav.sectorRrg') }}</el-menu-item>
          <el-menu-item index="/market">{{ t('nav.market') }}</el-menu-item>
          <el-menu-item index="/fund-flow">{{ t('nav.fundFlow') }}</el-menu-item>
          <el-menu-item index="/earnings-calendar">{{ t('nav.earningsCalendar') }}</el-menu-item>
          <el-menu-item index="/reports">{{ t('nav.reports') }}</el-menu-item>
        </el-sub-menu>

        <!-- 2. 因子与标的 -->
        <el-sub-menu index="alpha-research-group">
          <template #title><span>{{ t('nav.group.alphaResearch') }}</span></template>
          <el-menu-item index="/factors">{{ t('nav.factors') }}</el-menu-item>
          <el-menu-item index="/factor-backtest">{{ t('nav.factorBacktest') }}</el-menu-item>
          <el-menu-item index="/screener">{{ t('nav.screener') }}</el-menu-item>
          <el-menu-item index="/funds">{{ t('nav.funds') }}</el-menu-item>
          <el-menu-item index="/stock-compare">{{ t('nav.stockCompare') }}</el-menu-item>
          <el-menu-item index="/watchlist">{{ t('nav.watchlist') }}</el-menu-item>
        </el-sub-menu>

        <!-- 3. 策略与回测 -->
        <el-sub-menu index="strategy-lab-group">
          <template #title><span>{{ t('nav.group.strategyLab') }}</span></template>
          <el-menu-item index="/strategy-lab">{{ t('nav.strategyLab') }}</el-menu-item>
          <el-menu-item index="/case-library">{{ t('nav.caseLibrary') }}</el-menu-item>
          <el-menu-item index="/portfolio-backtest">{{ t('nav.portfolioBacktest') }}</el-menu-item>
          <el-menu-item index="/options">{{ t('nav.options') }}</el-menu-item>
          <el-menu-item index="/cbond">{{ t('nav.cbond') }}</el-menu-item>
          <el-menu-item index="/arbitrage">{{ t('nav.arbitrage') }}</el-menu-item>
        </el-sub-menu>

        <!-- 4. 监控与执行 -->
        <el-sub-menu index="execution-monitor-group">
          <template #title><span>{{ t('nav.group.executionMonitor') }}</span></template>
          <el-menu-item index="/paper-deployments">{{ t('nav.paperDeployments') }}</el-menu-item>
          <el-menu-item index="/anomaly-scanner">{{ t('nav.anomalyScanner') }}</el-menu-item>
          <el-menu-item index="/alerts">{{ t('nav.alerts') }}</el-menu-item>
        </el-sub-menu>

        <!-- 5. 组合与风控 -->
        <el-sub-menu index="portfolio-risk-group">
          <template #title><span>{{ t('nav.group.portfolioRisk') }}</span></template>
          <el-menu-item index="/portfolio">{{ t('nav.portfolio') }}</el-menu-item>
          <el-menu-item index="/retrospective">{{ t('nav.retrospective') }}</el-menu-item>
        </el-sub-menu>

        <!-- 6. 投研工作台 -->
        <el-sub-menu index="workbench-group">
          <template #title><span>{{ t('nav.group.workbench') }}</span></template>
          <el-menu-item index="/">{{ t('nav.analyze') }}</el-menu-item>
          <el-menu-item index="/thesis">{{ t('nav.thesisWorkbench') }}</el-menu-item>
          <el-menu-item index="/auto-research">{{ t('nav.autoResearch') }}</el-menu-item>
          <el-menu-item index="/research-ops">{{ t('nav.researchOps') }}</el-menu-item>
          <el-menu-item index="/chat">{{ t('nav.chat') }}</el-menu-item>
          <el-menu-item index="/batch">{{ t('nav.batch') }}</el-menu-item>
          <el-menu-item index="/tasks">{{ t('nav.tasks') }}</el-menu-item>
          <el-menu-item index="/history">{{ t('nav.history') }}</el-menu-item>
          <!-- ALENS-193: admin 入口不对普通用户暴露 (路由守卫 + 后端 403 兜底);
               顺带补上 settings 项此前缺失的视觉门 (此前只有守卫在拦) -->
          <el-menu-item v-if="currentUser?.role === 'admin'" index="/agent-backtest">{{ t('nav.agentBacktest') }}</el-menu-item>
          <el-menu-item v-if="currentUser?.role === 'admin'" index="/settings">{{ t('nav.settings') }}</el-menu-item>
        </el-sub-menu>
      </el-menu>

      <div class="header-actions">
        <span v-if="currentUser" class="user-pill">
          👤 {{ currentUser.username }}
          <el-tag v-if="currentUser.role === 'admin'" size="small" type="danger" style="margin-left: 4px;">{{ t('nav.admin') }}</el-tag>
        </span>
        <el-tooltip :content="t('theme.toggle.toDark')" placement="bottom">
          <el-button :icon="darkMode ? Sunny : Moon" circle @click="toggleTheme" />
        </el-tooltip>
        <el-tooltip :content="lang === 'zh' ? 'English' : '中文'" placement="bottom">
          <el-button
            circle
            @click="toggleLang"
            style="margin-left: 8px; font-weight: 600;"
          >
            {{ lang === 'zh' ? '中' : 'EN' }}
          </el-button>
        </el-tooltip>
        <el-button
          v-if="currentUser"
          text
          type="danger"
          style="margin-left: 8px;"
          @click="handleLogout"
        >
          退出
        </el-button>
      </div>
    </el-header>

    <!-- 移动/平板端 header -->
    <el-header v-else class="header-mobile">
      <div class="brand">
        <span class="logo">alphalens</span>
      </div>
      <div class="header-actions">
        <span v-if="currentUser" class="user-pill mobile">{{ currentUser.username }}</span>
        <el-button :icon="darkMode ? Sunny : Moon" circle @click="toggleTheme" size="small" />
        <el-button
          circle
          @click="toggleLang"
          size="small"
          style="margin-left: 6px; font-weight: 600;"
        >
          {{ lang === 'zh' ? '中' : 'EN' }}
        </el-button>
        <el-button
          :icon="drawerOpen ? Close : MenuIcon"
          circle
          @click="drawerOpen = !drawerOpen"
          size="small"
          style="margin-left: 6px;"
        />
      </div>
    </el-header>

    <!-- 移动/平板端 drawer (导航) -->
    <el-drawer
      v-if="!isDesktop()"
      v-model="drawerOpen"
      direction="ltr"
      :with-header="false"
      size="280px"
    >
      <div class="drawer-content">
        <div class="drawer-header">
          <span class="logo">alphalens</span>
          <span class="tag">导航</span>
        </div>
        <el-menu
          :default-active="activeMenu"
          router
          class="drawer-menu"
          @select="drawerOpen = false"
        >
          <el-menu-item v-for="item in navItems" :key="item.index" :index="item.index">
            <span class="drawer-label">{{ item.label }}</span>
          </el-menu-item>
        </el-menu>
        <div class="drawer-footer" style="padding: 16px;">
          <el-button v-if="currentUser" type="danger" block style="width: 100%;" @click="handleLogout">
            {{ t('auth.logout') }}
          </el-button>
        </div>
      </div>
    </el-drawer>

    <el-main class="main">
      <RouterView v-slot="{ Component }">
        <keep-alive :include="KEEP_ALIVE_VIEWS" :max="KEEP_ALIVE_VIEWS.length">
          <component :is="Component" />
        </keep-alive>
      </RouterView>
    </el-main>
    <el-footer class="footer">
      <span>© TickerLab · alphalens v0.2.0</span>
    </el-footer>
  </el-container>
</template>

<style scoped>
.layout, .login-layout { min-height: 100vh; background-color: var(--bg-secondary); }

/* 桌面 header */
.header {
  display: flex;
  align-items: center;
  gap: 20px;
  height: 60px !important;
  position: sticky;
  top: 0;
  z-index: 1000;
  border-bottom: 1px solid var(--border-color);
  padding: 0 24px;
  background-color: var(--bg-elevated);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  transition: background-color 0.2s ease, border-color 0.2s ease;
  flex-wrap: nowrap;
}

.brand {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.logo {
  font-size: 20px;
  font-weight: 800;
  letter-spacing: -0.5px;
  background: linear-gradient(135deg, #10b981 0%, #3b82f6 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.tag {
  font-size: 11px;
  color: var(--text-tertiary);
  padding: 2px 6px;
  background-color: var(--bg-primary);
  border-radius: 4px;
  border: 1px solid var(--border-color);
  font-weight: 500;
}

.nav-menu {
  border: none;
  flex: 1;
  background: transparent;
  min-width: 0;
}

.header-actions {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  margin-left: auto;
}

.user-pill {
  font-size: 13px;
  font-weight: 600;
  padding: 4px 10px;
  background: var(--bg-primary);
  border-radius: 16px;
  border: 1px solid var(--border-color);
  margin-right: 12px;
  display: flex;
  align-items: center;
}

.user-pill.mobile {
  font-size: 11px;
  padding: 2px 6px;
  margin-right: 6px;
}

/* 移动端 header */
.header-mobile {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 52px !important;
  position: sticky;
  top: 0;
  z-index: 1000;
  border-bottom: 1px solid var(--border-color);
  padding: 0 16px;
  background-color: var(--bg-elevated);
}

.main {
  padding: 20px;
  max-width: 1600px;
  margin: 0 auto;
  width: 100%;
}

.footer {
  text-align: center;
  font-size: 12px;
  color: var(--text-tertiary);
  padding: 16px;
  border-top: 1px solid var(--border-color);
  background-color: var(--bg-elevated);
}

.drawer-content {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.drawer-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px;
  border-bottom: 1px solid var(--border-color);
}

.drawer-menu {
  border: none;
  flex: 1;
}

.drawer-icon {
  margin-right: 12px;
}
</style>
